import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import SearchBar from '../components/SearchBar';
import ServiceCard from '../components/ServiceCard';
import CategoryStrip from '../components/CategoryPill';
import usePullToRefresh from '../hooks/usePullToRefresh';
import useCurrencyStore from '../store/useCurrencyStore';
import api from '../services/api';

export default function ExplorePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('');
  const { currency } = useCurrencyStore();
  const currLabel = currency?.code || 'KWD';
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    listingType: searchParams.get('listingType') || '',
    modelType: '',
    minPrice: '',
    maxPrice: '',
    location: ''
  });
  const [searchHistory, setSearchHistory] = useState(() =>
    JSON.parse(localStorage.getItem('findr-search-history') || '[]')
  );
  const [showHistory, setShowHistory] = useState(false);

  const query = searchParams.get('q') || '';

  const saveToHistory = (q) => {
    if (!q.trim()) return;
    const next = [q, ...searchHistory.filter(h => h !== q)].slice(0, 5);
    setSearchHistory(next);
    localStorage.setItem('findr-search-history', JSON.stringify(next));
  };

  const removeFromHistory = (item) => {
    const next = searchHistory.filter(h => h !== item);
    setSearchHistory(next);
    localStorage.setItem('findr-search-history', JSON.stringify(next));
  };

  const clearHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem('findr-search-history');
  };

  const handleSearch = (q) => {
    saveToHistory(q);
    setSearchParams({ q });
    setShowHistory(false);
  };

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query) params.set('search', query);
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      if (sortBy) params.set('sortBy', sortBy);
      const { data } = await api.get(`/services?${params}`);
      let results = data.services || [];
      // Client-side filter for model type (face / no face)
      if (filters.modelType) results = results.filter(s => s.tags?.includes(filters.modelType));
      // Client-side sort for rating/price since backend may not support it yet
      if (sortBy === 'rating') results = [...results].sort((a, b) => (b.provider?.rating || 0) - (a.provider?.rating || 0));
      if (sortBy === 'price_asc') results = [...results].sort((a, b) => (a.price || 0) - (b.price || 0));
      if (sortBy === 'price_desc') results = [...results].sort((a, b) => (b.price || 0) - (a.price || 0));
      setServices(results);
      setTotal(data.total || 0);
    } catch {}
    setLoading(false);
  }, [query, filters, sortBy]);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  const { pulling, pullDistance } = usePullToRefresh(fetchServices);

  const handleCategorySelect = (cat) => {
    setFilters(f => ({ ...f, category: cat || '', modelType: cat === 'MODELS' ? f.modelType : '' }));
  };

  return (
    <div className="min-h-screen bg-dark-bg pb-24">
      {/* Pull to refresh indicator */}
      {pullDistance > 0 && (
        <div className="flex items-center justify-center overflow-hidden transition-all duration-150" style={{ height: Math.min(pullDistance * 0.6, 56) }}>
          <div className="flex items-center gap-2">
            {pulling
              ? <><div className="w-4 h-4 border-2 border-brand-purple border-t-transparent rounded-full animate-spin" /><span className="text-xs text-brand-purple font-medium">Release to refresh</span></>
              : <span className="text-xs text-dark-subtext">Pull to refresh</span>
            }
          </div>
        </div>
      )}

      {/* Header */}
      <div className="px-4 pt-12 pb-4">
        <h1 className="text-2xl font-bold text-dark-text mb-4" style={{ letterSpacing: '-0.5px' }}>Explore</h1>
        <div
          className="relative"
          onFocus={() => setShowHistory(true)}
          onBlur={() => setTimeout(() => setShowHistory(false), 150)}
        >
          <SearchBar placeholder="Search services, studios..." onSearch={handleSearch} />
          {showHistory && !query && searchHistory.length > 0 && (
            <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-dark-surface border border-dark-border rounded-2xl overflow-hidden shadow-lg">
              <div className="flex items-center justify-between px-4 py-2 border-b border-dark-border">
                <span className="text-xs font-semibold text-dark-subtext uppercase tracking-wide">Recent searches</span>
                <button onClick={clearHistory} className="text-xs text-brand-purple font-medium">Clear all</button>
              </div>
              {searchHistory.map((item) => (
                <div key={item} className="flex items-center gap-3 px-4 py-3 hover:bg-dark-muted/30 border-b border-dark-border last:border-b-0">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-dark-subtext flex-shrink-0">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                  <button
                    className="flex-1 text-left text-sm text-dark-text"
                    onMouseDown={(e) => { e.preventDefault(); handleSearch(item); }}
                  >
                    {item}
                  </button>
                  <button
                    onMouseDown={(e) => { e.preventDefault(); removeFromHistory(item); }}
                    className="text-dark-subtext/60 hover:text-dark-subtext text-xs px-1"
                    aria-label="Remove"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Categories */}
      <div className="px-4 mb-4">
        <CategoryStrip selected={filters.category || null} onSelect={handleCategorySelect} />
      </div>

      {/* Model type sub-filter (only when Models category selected) */}
      {filters.category === 'MODELS' && (
        <div className="px-4 mb-3 flex gap-2">
          {[{ value: '', label: 'All Models' }, { value: 'FACE', label: 'Face Shown' }, { value: 'NO_FACE', label: 'No Face' }].map(opt => (
            <button
              key={opt.value}
              onClick={() => setFilters(f => ({ ...f, modelType: opt.value }))}
              className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                filters.modelType === opt.value
                  ? 'border-brand-purple text-brand-purple bg-brand-purple/10'
                  : 'border-dark-border text-dark-subtext'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* Filter bar */}
      <div className="px-4 mb-4 flex items-center gap-3">
        <button
          onClick={() => setShowFilters(v => !v)}
          className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
            showFilters ? 'border-brand-purple text-brand-purple' : 'border-dark-border text-dark-subtext'
          }`}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="4" y1="6" x2="20" y2="6"/>
            <line x1="8" y1="12" x2="16" y2="12"/>
            <line x1="11" y1="18" x2="13" y2="18"/>
          </svg>
          Filters
        </button>
        <button
          onClick={() => setFilters(f => ({ ...f, listingType: f.listingType === 'SERVICE' ? '' : 'SERVICE' }))}
          className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
            filters.listingType === 'SERVICE'
              ? 'border-brand-purple text-brand-purple bg-brand-purple/10'
              : 'border-dark-border text-dark-subtext'
          }`}
        >
          Services
        </button>
        <button
          onClick={() => setFilters(f => ({ ...f, listingType: f.listingType === 'STUDIO' ? '' : 'STUDIO' }))}
          className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
            filters.listingType === 'STUDIO'
              ? 'border-brand-purple text-brand-purple bg-brand-purple/10'
              : 'border-dark-border text-dark-subtext'
          }`}
        >
          Studios
        </button>
        <span className="text-dark-subtext text-xs ml-auto">{total} results</span>
      </div>

      {/* Expanded filters panel */}
      {showFilters && (
        <div className="mx-4 mb-4 p-4 bg-dark-surface border border-dark-border rounded-2xl">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-dark-subtext mb-1 block">Min Price ({currLabel})</label>
              <input
                type="number"
                value={filters.minPrice}
                onChange={e => setFilters(f => ({ ...f, minPrice: e.target.value }))}
                placeholder="0"
                className="w-full bg-dark-bg border border-dark-border rounded-xl px-3 py-2 text-dark-text text-sm outline-none focus:border-brand-purple"
              />
            </div>
            <div>
              <label className="text-xs text-dark-subtext mb-1 block">Max Price ({currLabel})</label>
              <input
                type="number"
                value={filters.maxPrice}
                onChange={e => setFilters(f => ({ ...f, maxPrice: e.target.value }))}
                placeholder="Any"
                className="w-full bg-dark-bg border border-dark-border rounded-xl px-3 py-2 text-dark-text text-sm outline-none focus:border-brand-purple"
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-dark-subtext mb-1 block">Location</label>
              <input
                type="text"
                value={filters.location}
                onChange={e => setFilters(f => ({ ...f, location: e.target.value }))}
                placeholder="Kuwait City, Salmiya, Hawalli..."
                className="w-full bg-dark-bg border border-dark-border rounded-xl px-3 py-2 text-dark-text text-sm outline-none focus:border-brand-purple"
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-dark-subtext mb-1 block">Sort By</label>
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value)}
                  className="w-full bg-dark-bg border border-dark-border rounded-xl px-3 py-2 text-dark-text text-sm outline-none focus:border-brand-purple appearance-none"
                >
                  <option value="">Default</option>
                  <option value="rating">Top Rated</option>
                  <option value="price_asc">Price: Low to High</option>
                  <option value="price_desc">Price: High to Low</option>
                </select>
                <svg className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6B6B7A" strokeWidth="2">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </div>
            </div>
          </div>
          <button
            onClick={() => { setFilters({ category: '', listingType: '', modelType: '', minPrice: '', maxPrice: '', location: '' }); setSortBy(''); }}
            className="mt-3 text-brand-purple text-sm font-medium"
          >
            Clear all filters
          </button>
        </div>
      )}

      {/* Results */}
      <div className="px-4">
        {loading ? (
          <div className="grid grid-cols-2 gap-3">
            {[...Array(6)].map((_, i) => <div key={i} className="h-48 bg-dark-surface rounded-2xl animate-pulse" />)}
          </div>
        ) : services.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-4xl mb-3">🔍</p>
            <p className="text-dark-text font-semibold mb-1">No results found</p>
            <p className="text-dark-subtext text-sm">Try adjusting your filters or search terms</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {services.map(s => <ServiceCard key={s.id} service={s} />)}
          </div>
        )}
      </div>
    </div>
  );
}
